"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CoachMarkStep {
  /** Selector CSS del elemento a "iluminar" */
  target: string;
  title: string;
  description: string;
  placement?: "top" | "bottom" | "left" | "right";
  /** Si se cumple esta condición, el step se marca como done automáticamente. */
  trigger?: () => boolean;
}

interface CoachMarksProps {
  /** Si el tour está activo. */
  active: boolean;
  steps: CoachMarkStep[];
  /** Callback al completar o cerrar. */
  onComplete: () => void;
  /** Step inicial (0 por defecto). */
  initialStep?: number;
}

/**
 * Tour guiado paso a paso. Resalta un elemento con un spotlight
 * y muestra un tooltip con descripción + botón "Siguiente".
 * Re-renderiza el spotlight al scrollear o redimensionar.
 *
 * Uso:
 *   <CoachMarks
 *     active={tourActive}
 *     steps={[
 *       { target: '#editor', title: 'Tu editor', description: '...' },
 *     ]}
 *     onComplete={() => setTourActive(false)}
 *   />
 */
export function CoachMarks({
  active,
  steps,
  onComplete,
  initialStep = 0,
}: CoachMarksProps) {
  const [stepIndex, setStepIndex] = useState(initialStep);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const currentStep = steps[stepIndex];

  const updateRect = () => {
    if (!currentStep) return;
    const el = document.querySelector(currentStep.target);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    requestAnimationFrame(() => {
      setRect(el.getBoundingClientRect());
    });
  };

  useEffect(() => {
    if (!active) {
      setStepIndex(initialStep);
      setRect(null);
      return;
    }
    updateRect();

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [active, stepIndex]);

  if (!mounted || !active || !currentStep) return null;

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => onComplete();

  const tooltipPosition = computeTooltipPosition(rect, currentStep.placement ?? "bottom");

  return createPortal(
    <div className="fixed inset-0 z-[400] pointer-events-none">
      {/* Overlay con cutout */}
      {rect && (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" aria-hidden="true">
          <defs>
            <mask id="coach-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.55)"
            mask="url(#coach-mask)"
            onClick={handleSkip}
          />
        </svg>
      )}

      {/* Spotlight ring */}
      {rect && (
        <div
          className="absolute pointer-events-none border-2 border-primary rounded-lg shadow-[0_0_0_4px_rgba(239,68,68,0.2)] transition-all"
          style={{
            left: rect.left - 6,
            top: rect.top - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
          aria-hidden="true"
        />
      )}

      {/* Tooltip */}
      {rect && (
        <motion.div
          key={stepIndex}
          initial={{ opacity: 0, scale: 0.95, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          role="dialog"
          aria-labelledby="coach-title"
          className="absolute pointer-events-auto w-80 rounded-xl border border-border/60 bg-popover p-4 shadow-2xl"
          style={{
            left: tooltipPosition.left,
            top: tooltipPosition.top,
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary/70">
                {stepIndex + 1} / {steps.length}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="p-0.5 rounded text-muted-foreground/60 hover:text-foreground hover:bg-secondary/60 transition-colors"
              aria-label="Cerrar tour"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <h3
            id="coach-title"
            className="text-sm font-bold text-foreground mb-1 leading-tight"
          >
            {currentStep.title}
          </h3>
          <p className="text-xs text-muted-foreground leading-relaxed text-pretty">
            {currentStep.description}
          </p>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
            <button
              onClick={handleSkip}
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Omitir tour
            </button>
            <button
              onClick={handleNext}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium",
                "bg-primary text-primary-foreground hover:bg-primary/90 transition-colors",
              )}
            >
              {stepIndex === steps.length - 1 ? "Finalizar" : "Siguiente"}
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </motion.div>
      )}

      <AnimatePresence />
    </div>,
    document.body,
  );
}

function computeTooltipPosition(
  rect: DOMRect | null,
  placement: "top" | "bottom" | "left" | "right"
): { left: number; top: number } {
  if (!rect) return { left: 0, top: 0 };
  const margin = 16;
  const w = 320;
  const h = 180;
  switch (placement) {
    case "top":
      return {
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - w - margin)),
        top: Math.max(margin, rect.top - h - margin),
      };
    case "bottom":
      return {
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - w - margin)),
        top: rect.bottom + margin,
      };
    case "left":
      return {
        left: Math.max(margin, rect.left - w - margin),
        top: Math.max(margin, rect.top),
      };
    case "right":
      return {
        left: Math.min(window.innerWidth - w - margin, rect.right + margin),
        top: Math.max(margin, rect.top),
      };
  }
}
